import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Search, Filter, ChevronRight, Clock, MapPin, Phone, FileCheck2, Brain, Shuffle, AlertTriangle, ShieldCheck, CheckCircle2, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import RuntimeSourcePanel from '../shared/RuntimeSourcePanel';
import VerificationBadge from '../shared/VerificationBadge';

const STATUS_COLORS = {
  intake: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  draft: 'bg-purple-100 text-purple-700',
  review: 'bg-orange-100 text-orange-700',
  execution: 'bg-teal-100 text-teal-700',
  completed: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700',
  blocked: 'bg-red-100 text-red-700',
};

const RUNTIME_ICONS = {
  'template-driven': FileCheck2,
  'hybrid': Shuffle,
  'adaptive-only': Brain,
  'unresolved': Clock,
};

function renderPayloadFields(obj, depth = 0) {
  if (obj === null || obj === undefined) return <span className="text-gray-400">—</span>;
  if (typeof obj === 'string') return <span className="text-gray-900">{obj}</span>;
  if (typeof obj === 'number' || typeof obj === 'boolean') return <span className="text-gray-900">{String(obj)}</span>;
  if (Array.isArray(obj)) {
    if (obj.length === 0) return <span className="text-gray-400">[]</span>;
    return (
      <div className="space-y-1">
        {obj.map((item, i) => (
          <div key={i} className="border-l-2 border-gray-200 pl-3">
            <span className="text-[10px] font-mono text-gray-400">[{i}]</span>
            {renderPayloadFields(item, depth + 1)}
          </div>
        ))}
      </div>
    );
  }
  if (typeof obj === 'object') {
    const entries = Object.entries(obj);
    if (entries.length === 0) return <span className="text-gray-400">{'{}'}</span>;
    return (
      <div className="space-y-1.5">
        {entries.map(([key, val]) => (
          <div key={key} className="grid grid-cols-[200px_1fr] gap-2">
            <span className="text-xs font-mono text-gray-500 truncate">{key}</span>
            <div className="text-sm">{renderPayloadFields(val, depth + 1)}</div>
          </div>
        ))}
      </div>
    );
  }
  return <span className="text-gray-900">{String(obj)}</span>;
}

export default function AdminCases() {
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseMessages, setCaseMessages] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [artifacts, setArtifacts] = useState([]);
  const [secureDocs, setSecureDocs] = useState([]);
  const [payloadData, setPayloadData] = useState(null);
  const [editingPayload, setEditingPayload] = useState(false);
  const [editedPayload, setEditedPayload] = useState('');

  useEffect(() => {
    loadCases();
  }, [page, statusFilter]);

  const loadCases = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get('/admin/cases', { params });
      setCases(data.cases);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load cases:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === cases.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(cases.map(c => c.id)));
  };

  const deleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} case${ids.length > 1 ? 's' : ''} and all related data? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.post('/admin/cases/delete-bulk', { case_ids: ids });
      toast.success(`Deleted ${ids.length} case${ids.length > 1 ? 's' : ''}`);
      setSelectedIds(new Set());
      loadCases();
    } catch (err) {
      toast.error('Delete failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const deleteAllCases = async () => {
    if (!window.confirm('Delete ALL cases and all related data? This cannot be undone.')) return;
    if (!window.confirm('Are you absolutely sure? ALL cases across all pages will be permanently removed.')) return;
    setDeleting(true);
    try {
      const { data } = await api.post('/admin/cases/delete-all');
      toast.success(`Deleted ${data.cases || 0} cases`);
      setSelectedIds(new Set());
      setPage(1);
      loadCases();
    } catch (err) {
      toast.error('Delete all failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const loadCaseMessages = async (caseId) => {
    try {
      const { data } = await api.get(`/chat/history/${caseId}`);
      setCaseMessages(data.messages);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const fetchArtifacts = async (caseId) => {
    try {
      const { data } = await api.get(`/admin/cases/${caseId}/artifacts`);
      setArtifacts(data.artifacts || []);
    } catch (err) {
      console.error('Failed to load artifacts:', err);
    }
  };

  const fetchPayload = async (caseId) => {
    try {
      const { data } = await api.get(`/admin/cases/${caseId}/automation-payload`);
      if (data && data.payload_json) {
        setPayloadData(data);
        setEditedPayload(JSON.stringify(data.payload_json, null, 2));
        return;
      }
    } catch (err) {
      console.error('Failed to load payload:', err);
    }
    const efCtx = selectedCase?.efiling_context || {};
    if (efCtx.pdf_available_payload) {
      const fallback = { payload_json: efCtx.pdf_available_payload, payload_status: efCtx.status || 'extracted', missing_fields: efCtx.pdf_available_payload.missing_fields || [] };
      setPayloadData(fallback);
      setEditedPayload(JSON.stringify(fallback.payload_json, null, 2));
    }
  };

  const savePayload = async () => {
    try {
      const parsed = JSON.parse(editedPayload);
      await api.patch(`/admin/cases/${selectedCase.id}/automation-payload`, { payload_json: parsed });
      setPayloadData({ ...payloadData, payload_json: parsed });
      setEditingPayload(false);
      toast.success('Payload updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save payload');
    }
  };

  const fetchSecureDocs = async (caseId) => {
    try {
      const { data } = await api.get(`/documents/${caseId}/list`);
      setSecureDocs(data.documents || []);
    } catch (err) {
      setSecureDocs([]);
    }
  };

  const openCase = (c) => {
    setSelectedCase(c);
    setCaseMessages([]);
    setArtifacts([]);
    setSecureDocs([]);
    setPayloadData(null);
    setEditingPayload(false);
    loadCaseMessages(c.id);
    fetchArtifacts(c.id);
    fetchSecureDocs(c.id);
    fetchPayload(c.id);
  };

  const updateCaseStatus = async (field, value) => {
    try {
      const { data } = await api.patch(`/admin/cases/${selectedCase.id}`, { [field]: value });
      setSelectedCase(data);
      toast.success('Status updated');
      loadCases(); // Refresh list in background
    } catch (err) {
      toast.error('Update failed');
    }
  };

  if (selectedCase) {
    return (
      <div data-testid="case-detail-view">
        <button
          data-testid="back-to-cases-btn"
          onClick={() => { setSelectedCase(null); setCaseMessages([]); }}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 mb-6 transition"
        >
          <ChevronRight className="w-4 h-4 rotate-180" /> Back to cases
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold">{selectedCase.case_number}</h1>
            <div className="flex items-center gap-3 mt-1">
              <span className={`px-2 py-0.5 text-xs font-mono uppercase ${STATUS_COLORS[selectedCase.status] || 'bg-gray-100'} rounded-sm`}>
                {selectedCase.status}
              </span>
              <span className="text-xs text-gray-400 font-mono">{selectedCase.source_channel}</span>
            </div>
          </div>
        </div>

        {/* E-Stamp & E-Sign Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <section className="border border-gray-200 p-4 bg-white rounded-md shadow-sm">
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-indigo-500" /> E-Stamp Status
            </h3>
            <div className="flex items-center justify-between">
               <span className={`px-2 py-1 text-[10px] font-mono uppercase rounded font-bold ${
                 selectedCase.e_stamp_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                 selectedCase.e_stamp_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                 'bg-slate-100 text-slate-500'
               }`}>
                 {selectedCase.e_stamp_status || 'not_required'}
               </span>
               <div className="flex gap-2">
                 <button onClick={() => updateCaseStatus('e_stamp_status', 'pending')} className="text-[10px] bg-slate-800 text-white px-2.5 py-1.5 rounded hover:bg-slate-900 transition">Mark Pending</button>
                 <button onClick={() => updateCaseStatus('e_stamp_status', 'completed')} className="text-[10px] bg-emerald-600 text-white px-2.5 py-1.5 rounded hover:bg-emerald-700 transition">Mark Completed</button>
               </div>
            </div>
          </section>
          <section className="border border-gray-200 p-4 bg-white rounded-md shadow-sm">
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> E-Sign Status
            </h3>
            <div className="flex items-center justify-between">
               <span className={`px-2 py-1 text-[10px] font-mono uppercase rounded font-bold ${
                 selectedCase.e_sign_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                 selectedCase.e_sign_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                 'bg-slate-100 text-slate-500'
               }`}>
                 {selectedCase.e_sign_status || 'not_required'}
               </span>
               <div className="flex gap-2">
                 <button onClick={() => updateCaseStatus('e_sign_status', 'pending')} className="text-[10px] bg-slate-800 text-white px-2.5 py-1.5 rounded hover:bg-slate-900 transition">Mark Pending</button>
                 <button onClick={() => updateCaseStatus('e_sign_status', 'completed')} className="text-[10px] bg-emerald-600 text-white px-2.5 py-1.5 rounded hover:bg-emerald-700 transition">Mark Completed</button>
               </div>
            </div>
          </section>
        </div>

        {/* Case Info Grid */}
        <div className="grid grid-cols-3 border border-gray-200 mb-6">
          <div className="p-4 border-r border-gray-200">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 block mb-1">Client</span>
            <p className="font-medium">{selectedCase.client_name || 'Not collected'}</p>
          </div>
          <div className="p-4 border-r border-gray-200">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 block mb-1">Service</span>
            <p className="font-medium">{selectedCase.service_type || 'Not collected'}</p>
          </div>
          <div className="p-4">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 block mb-1">Location</span>
            <p className="font-medium">{[selectedCase.city, selectedCase.state].filter(Boolean).join(', ') || 'Not collected'}</p>
          </div>
        </div>

        {/* Payment Status */}
        <div className="border border-gray-200 mb-6">
          <div className="px-4 py-3 border-b border-gray-200 bg-surface">
            <h3 className="font-mono text-xs uppercase tracking-widest text-gray-500">Payment Status</h3>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <span className={`px-2 py-1 text-xs font-mono uppercase font-bold rounded ${
                selectedCase.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                selectedCase.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                selectedCase.payment_status === 'partial' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-500'
              }`}>
                {selectedCase.payment_status || 'pending'}
              </span>
              {selectedCase.client_payment_amount && (
                <span className="text-sm text-gray-600">₹{Number(selectedCase.client_payment_amount).toLocaleString()}</span>
              )}
              {selectedCase.assignment_status && (
                <span className="text-xs text-gray-400 ml-auto">
                  Assignment: {selectedCase.assignment_status.replace(/_/g, ' ')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Secure Uploaded Documents */}
        {secureDocs.length > 0 && (
          <div className="border border-gray-200 mb-6">
            <div className="px-4 py-3 border-b border-gray-200 bg-surface">
              <h3 className="font-mono text-xs uppercase tracking-widest text-gray-500">
                Secure Documents ({secureDocs.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-100">
              {secureDocs.map((d) => (
                <div key={d.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-gray-500">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{d.display_name || d.file_name}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-gray-400 font-mono uppercase">{d.category}</span>
                        <span className={`text-xs font-medium ${d.encrypted !== false ? 'text-green-600' : 'text-amber-600'}`}>
                          {d.encrypted !== false ? 'Encrypted' : 'Unencrypted'}
                        </span>
                        {d.retention_until && (
                          <span className="text-xs text-gray-400">
                            Retention: {new Date(d.retention_until).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <a
                    href={`/api/documents/${d.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Download
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Uploaded Documents */}
        {artifacts.length > 0 && (
          <div className="border border-gray-200 mb-6">
            <div className="px-4 py-3 border-b border-gray-200 bg-surface">
              <h3 className="font-mono text-xs uppercase tracking-widest text-gray-500">
                Uploaded Documents ({artifacts.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-100">
              {artifacts.map((a) => (
                <div key={a.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-gray-500">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{a.file_name}</p>
                      {a.file_size && <p className="text-xs text-gray-400">{Math.round(Number(a.file_size) / 1024)} KB</p>}
                    </div>
                  </div>
                  <a
                    href={`/api/admin/artifacts/${a.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Download
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Extracted Payload */}
        {payloadData && (
          <div className="border border-gray-200 mb-6">
            <div className="px-4 py-3 border-b border-gray-200 bg-surface flex items-center justify-between">
              <h3 className="font-mono text-xs uppercase tracking-widest text-gray-500">
                Extracted Payload — {payloadData.payload_status || 'unknown'}
              </h3>
              <button
                onClick={() => setEditingPayload(!editingPayload)}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                {editingPayload ? 'Cancel' : 'Edit'}
              </button>
            </div>
            <div className="p-4">
              {editingPayload ? (
                <div className="space-y-3">
                  <textarea
                    value={editedPayload}
                    onChange={(e) => setEditedPayload(e.target.value)}
                    className="w-full h-64 border border-gray-300 rounded text-xs font-mono p-3 focus:outline-none focus:ring-1 focus:ring-black"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={savePayload}
                      className="px-3 py-1.5 text-xs bg-black text-white rounded hover:bg-gray-800"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingPayload(false)}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  {renderPayloadFields(payloadData.payload_json)}
                </div>
              )}
              {payloadData.missing_fields && payloadData.missing_fields.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-amber-600 font-medium mb-1">Missing fields ({payloadData.missing_fields.length}):</p>
                  <div className="flex flex-wrap gap-1">
                    {payloadData.missing_fields.map((f, i) => (
                      <span key={i} className="px-2 py-0.5 text-[10px] bg-amber-50 text-amber-700 rounded">{f}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Runtime source explainability */}
        <div className="mb-6">
          <RuntimeSourcePanel caseObj={selectedCase} />
        </div>

        {/* Conversation History */}
        <div className="border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200 bg-surface">
            <h3 className="font-mono text-xs uppercase tracking-widest text-gray-500">Conversation History</h3>
          </div>
          <div className="max-h-96 overflow-y-auto p-4 space-y-3">
            {caseMessages.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No messages yet</p>
            ) : caseMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-4 py-2.5 text-sm ${
                  msg.sender_type === 'client'
                    ? 'bg-black text-white rounded-xl rounded-tr-sm'
                    : 'bg-surface border border-gray-200 rounded-xl rounded-tl-sm'
                }`}>
                  {msg.content}
                  <div className={`text-[10px] mt-1 ${msg.sender_type === 'client' ? 'text-white/50' : 'text-gray-400'} font-mono`}>
                    {msg.sender_type} {msg.created_at ? new Date(msg.created_at).toLocaleTimeString() : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-cases">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Cases</h1>
          <p className="text-sm text-gray-500 mt-1">{total} total cases</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <button
              onClick={deleteSelected}
              disabled={deleting}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-red-600 text-white rounded-sm hover:bg-red-700 transition disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              Delete Selected ({selectedIds.size})
            </button>
          )}
          <button
            onClick={deleteAllCases}
            disabled={deleting}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-red-300 text-red-600 rounded-sm hover:bg-red-50 transition disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Delete All
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6">
        <select
          data-testid="case-status-filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-black"
        >
          <option value="">All Statuses</option>
          {Object.keys(STATUS_COLORS).map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {/* Cases Table */}
      <div className="border border-gray-200">
        <table className="w-full" data-testid="cases-table">
          <thead>
            <tr className="border-b border-gray-200 bg-surface">
              <th className="w-10 px-2 py-3 text-center">
                <input
                  type="checkbox"
                  checked={cases.length > 0 && selectedIds.size === cases.length}
                  onChange={toggleSelectAll}
                  className="accent-black cursor-pointer"
                  aria-label="Select all cases"
                />
              </th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Case #</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Client</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Phone</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Language</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Priority</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Status</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Assignment</th>
              <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400 text-sm">Loading...</td></tr>
            ) : cases.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400 text-sm">No cases found</td></tr>
            ) : cases.map((c) => {
              const RuntimeIcon = RUNTIME_ICONS[c.runtime_source] || Clock;
              const sel = c.template_selection || {};
              return (
              <tr
                key={c.id}
                data-testid={`case-row-${c.case_number}`}
                onClick={() => openCase(c)}
                className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition"
              >
                <td className="w-10 px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(c.id)}
                    onChange={() => toggleSelect(c.id)}
                    className="accent-black cursor-pointer"
                    aria-label={`Select ${c.case_number}`}
                  />
                </td>
                <td className="px-4 py-3 font-mono text-sm font-medium">{c.case_number}</td>
                <td className="px-4 py-3 text-sm">{c.client_name || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  <div className="flex items-center gap-1.5">
                    <Phone className="w-3 h-3 text-gray-400" />
                    {c.client_mobile || '—'}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-gray-500">{c.language?.toUpperCase() || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs font-mono uppercase rounded-sm ${
                    c.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                    c.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                    c.priority === 'low' ? 'bg-gray-100 text-gray-500' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {c.priority || 'normal'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs font-mono uppercase ${STATUS_COLORS[c.status] || 'bg-gray-100'} rounded-sm`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{c.assignment_status?.replace(/_/g, ' ')}</td>
                <td className="px-4 py-3 text-sm text-gray-400 font-mono">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500">Page {page} of {Math.ceil(total / 20)}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-sm disabled:opacity-30">Prev</button>
            <button disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-sm disabled:opacity-30">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
